/**
 * Centralized product configuration
 * Maps API product names to internal names, display names, and route prefixes
 */

/** Internal names aligned with PRODUCT_CONFIG (prefer over string literals in menu code). */
export const ProductInternalName = {
	KontrolEdge: "Kontrol Edge",
	CtxReflector: "CTX Reflector",
	CtxReflectorV2: "CTX Reflector V2",
	Insight: "Insight",
} as const;

export type ProductInternalNameId = (typeof ProductInternalName)[keyof typeof ProductInternalName];

/** Legacy route bundle not in PRODUCT_CONFIG (nested under Kontrol menu data). */
const CTX_HUB_INTERNAL_NAME = "CTX Hub";

export interface ProductConfig {
	apiName: string; // Name from API (e.g., "Reflector", "Kontrol Edge")
	internalName: string; // Internal name used in code (e.g., "CTX Reflector")
	displayName: string; // Display name for UI (e.g., "Reflector v1.0")
	routePrefix: string; // Route prefix (e.g., "/reflector")
	/**
	 * Default landing path for this product (Home/Overview). Used by the post-login
	 * redirect and the product dropdown so both stay aligned with the menu's Home item.
	 */
	homePath: string;
}

/**
 * Product configuration mapping
 * Maps API product names to their configuration
 */
export const PRODUCT_CONFIG: Record<string, ProductConfig> = {
	"Reflector": {
		apiName: "Reflector",
		internalName: "CTX Reflector",
		displayName: "Reflector v1.0",
		routePrefix: "/reflector",
		// Reflector v1 uses `/` as Home (StatusPage is mounted at the index route)
		homePath: "/",
	},
	"Kontrol Edge": {
		apiName: "Kontrol Edge",
		internalName: "Kontrol Edge",
		displayName: "Kontrol Edge",
		routePrefix: "/kontrol",
		homePath: "/kontrol",
	},
	"Reflector V2": {
		apiName: "Reflector V2",
		internalName: "CTX Reflector V2",
		displayName: "Reflector v2.0",
		routePrefix: "/reflector-v2",
		homePath: "/reflector-v2/overview",
	},
	"Insight": {
		apiName: "Insight",
		internalName: "Insight",
		displayName: "Insight",
		routePrefix: "/insight",
		homePath: "/insight/home",
	},
};

/**
 * Default landing path for a product (Home/Overview). Falls back to its `routePrefix`
 * when no explicit homePath is set, and to `/` for unknown products.
 */
export const getProductHomePath = (internalName: string): string => {
	const cfg = getProductConfigByInternalName(internalName);
	if (!cfg) return "/";
	return cfg.homePath || cfg.routePrefix;
};

/**
 * Get product configuration by API name
 */
export const getProductConfig = (apiName: string): ProductConfig | undefined => {
	return PRODUCT_CONFIG[apiName];
};

/**
 * Get product configuration by internal name
 */
export const getProductConfigByInternalName = (internalName: string): ProductConfig | undefined => {
	return Object.values(PRODUCT_CONFIG).find((config) => config.internalName === internalName);
};

/**
 * Configs sorted by routePrefix length descending so longer prefixes match first
 * (e.g. /reflector-v2 before /reflector).
 */
function getProductConfigsByLongestRoutePrefix(): ProductConfig[] {
	return Object.values(PRODUCT_CONFIG).sort(
		(a, b) => b.routePrefix.length - a.routePrefix.length
	);
}

function pathMatchesProductRoute(path: string, routePrefix: string): boolean {
	return path === routePrefix || path.startsWith(`${routePrefix}/`);
}

/**
 * Single source of truth: pathname → product internal name (e.g. SideMenu dropdown sync).
 * Resolution order:
 *   1. Longest matching `routePrefix`
 *   2. Exact `homePath` match (covers products whose Home is outside their prefix, e.g. Reflector v1 at `/`)
 *   3. Legacy `/ctxhub`
 * Returns "" if unknown.
 */
export const resolveInternalNameFromPathname = (pathname: string): string => {
	const path = (pathname.split("?")[0] || "").trim();
	if (!path) {
		return "";
	}

	for (const config of getProductConfigsByLongestRoutePrefix()) {
		if (pathMatchesProductRoute(path, config.routePrefix)) {
			return config.internalName;
		}
	}

	const homeMatch = Object.values(PRODUCT_CONFIG).find(
		(config) => !!config.homePath && config.homePath === path
	);
	if (homeMatch) {
		return homeMatch.internalName;
	}

	if (pathMatchesProductRoute(path, "/ctxhub")) {
		return CTX_HUB_INTERNAL_NAME;
	}

	return "";
};

/**
 * Get product configuration by route (longest prefix wins).
 */
export const getProductConfigByRoute = (pathname: string): ProductConfig | undefined => {
	const path = pathname.split("?")[0] || "";
	for (const config of getProductConfigsByLongestRoutePrefix()) {
		if (pathMatchesProductRoute(path, config.routePrefix)) {
			return config;
		}
	}
	return undefined;
};

/**
 * Normalize product from API to include all configuration
 */
export interface NormalizedProduct {
	id: number;
	apiName: string;
	internalName: string;
	displayName: string;
	routePrefix: string;
	homePath: string;
}

export const normalizeProduct = (product: { id: number; name: string }): NormalizedProduct | null => {
	const config = getProductConfig(product.name);
	if (!config) {
		return null;
	}
	return {
		id: product.id,
		apiName: config.apiName,
		internalName: config.internalName,
		displayName: config.displayName,
		routePrefix: config.routePrefix,
		homePath: config.homePath,
	};
};

